'use strict';

import { fh_polygon } from '@acenv/fh-3d-viewer';
import { cn_building } from '../../model/cn_building';
import { cn_scene } from '../../model/cn_scene';
import { cn_storey } from '../../model/cn_storey';
import { cn_vertex } from '../../model/cn_vertex';
import { CN_MIDDLE, cn_wall } from '../../model/cn_wall';
import { cn_wall_type } from '../../model/cn_wall_type';
import { cn_add, cn_box, cn_dist, cn_dot, cn_intersect_line, cn_middle, cn_mul, cn_normal, cn_normalize, cn_size, cn_sub, cnx_clone } from '../cn_utilities';
import { cn_opening_type } from '../../model/cn_opening_type';
import { cn_opening } from '../../model/cn_opening';
import { cn_material } from '../../model/cn_material';
import { logger } from '../cn_logger';
import { CODES_BIM_OUVRAGES } from '../cn_bbp_constants';
import { cn_material_type, CN_MATERIAL_TYPE_OTHER } from '../../model/cn_material_type';

//***********************************************************************************
//***********************************************************************************
//**** WISEBIM importer
//***********************************************************************************
//***********************************************************************************

export function cn_import_bbs_library(bbs_library, building) {
    var reader = new cn_bbs_library(building);
    return reader.parse(bbs_library);
}

export class cn_bbs_library {
    constructor(building) {
        this._building = building;
    }

    //***********************************************************************************
    //**** Main function
    //***********************************************************************************
    parse(bbs_library) {
        if (typeof (bbs_library.CatalogueType) != 'string') return false;

        if (bbs_library.CatalogueType == "Paroi")
            return this._parse_wall_library(bbs_library);
        else if (bbs_library.CatalogueType == "Menuiserie")
            return this._parse_opening_library(bbs_library);

        return false;
    }

    /**
     * Returns an array of two integers : ths first one is the number of element types crteated.
     * the second is the number of updated element types
     * the third is the number of removed element types.
     * @returns {number[]}
     */
    _parse_wall_library(bbs_library) {
        let res = [0, 0, 0];
        const wall_types = this._building.get_wall_types().filter(wt => wt.get_extra_data("cw"));
        let obsolete_wall_types = wall_types.concat([]);

        bbs_library.Parois.forEach(paroi => {
            if (typeof (paroi.UniqueId) != 'string') return;
            if (typeof (paroi.Reference) != 'string') return;
            if (typeof (paroi.Attributs) != 'object') return;
            const uid = paroi.UniqueId;

            let wall_type = wall_types.find(wt => wt.ID == uid);
            if (wall_type) {
                res[1]++;
                let idx = obsolete_wall_types.indexOf(wall_type);
                if (idx >= 0) obsolete_wall_types.splice(idx, 1);
            }
            else {
                res[0]++;
                wall_type = new cn_wall_type(this._building);
                this._building.element_types.push(wall_type);
            }

            let methode = this._find_attribute(paroi, "Methode");
            if (methode == undefined) return;
            wall_type.ID = uid;
            wall_type.name = paroi.Reference;
            wall_type.readonly = true;
            wall_type.set_extra_data("cw", paroi);

            paroi.description = { label: "Données ClimaWin", value: [] };
            paroi.Attributs.forEach(at => {
                if (at.Libelle && at.Valeur) {
                    const dsc = { label: at.Libelle, value: at.Valeur };
                    if (at.Unite) dsc.unit = at.Unite;
                    paroi.description.value.push(dsc);
                }
            });

            wall_type.layers = [];
            if (typeof (paroi.Couches) == 'object') {
                wall_type.is_expert = true;
                wall_type.category = "facade";
                paroi.Couches.forEach(couche => {
                    let name = this._find_attribute(couche, "CodeEdibatecMat");
                    let thickness = this._find_attribute(couche, "EpaisseurMat");
                    let lambda = this._find_attribute(couche, "LambdaMat");
                    let mu = this._find_attribute(couche, "MuMat");
                    let cp = this._find_attribute(couche, "CpMat");
                    const material_type = new cn_material_type(this, name, 'cw', lambda, mu, cp, CN_MATERIAL_TYPE_OTHER, [150, 150, 150]);
                    const layer = new cn_material(this, material_type, thickness);
                    wall_type.layers.push(layer);
                });
            }

            if (wall_type.layers.length == 0) {
                let thickness = 0;
                let th = this._find_attribute(paroi, "Epaisseur");
                if (th) {
                    thickness = th;
                }
                else {
                    th = this._find_attribute(paroi, "EpaisseurStructure");
                    if (th) thickness += th;
                    th = this._find_attribute(paroi, "EpaisseurIsol");
                    if (th) thickness += th;
                    th = this._find_attribute(paroi, "EpaisseurIsolant");
                    if (th) thickness += th;
                    if (thickness < 0.001) thickness = 0.1;
                }
                wall_type.is_expert = false;
                wall_type.category = "generic";
                wall_type.layers = [cn_material.material_by_code(this._building, 'unknown', thickness)];
            }
        });

        //*** Remove obsolete element types */
        obsolete_wall_types.forEach(wt => {
            if (this._building.get_element_type_count(wt) == 0) {
                var index = this._building.element_types.indexOf(wt);
                if (index >= 0) {
                    this._building.element_types.splice(index, 1);
                    res[2]++;
                }
            }
        });
        return res;
    }

    /**
     * Returns an array of two integers : ths first one is the number of element types crteated.
     * the second is the number of updated element types
     * the third is the number of removed element types.
     * @returns {number[]}
     */
    _parse_opening_library(bbs_library) {
        let res = [0, 0, 0];
        const opening_types = this._building.get_opening_types().filter(wt => wt.get_extra_data("cw"));
        let obsolete_opening_types = opening_types.concat([]);

        bbs_library.Menuiseries.forEach(menuiserie => {
            if (typeof (menuiserie.UniqueId) != 'string') return;
            if (typeof (menuiserie.Reference) != 'string') return;
            if (typeof (menuiserie.Attributs) != 'object') return;
            if (typeof (menuiserie.Dimensions) != 'object') return;
            const uid = menuiserie.UniqueId;

            let opening_type = opening_types.find(wt => wt.ID == uid);
            if (opening_type) {
                res[1]++;
                let idx = obsolete_opening_types.indexOf(opening_type);
                if (idx >= 0) obsolete_opening_types.splice(idx, 1);
            }
            else {
                res[0]++;
                opening_type = new cn_opening_type(this._building);
                this._building.element_types.push(opening_type);
            }

            opening_type.ID = uid;
            opening_type.name = menuiserie.Reference;
            opening_type.readonly = true;
            opening_type.set_extra_data("cw", menuiserie);

            menuiserie.description = { label: "Données ClimaWin", value: [] };
            menuiserie.Attributs.forEach(at => {
                if (at.Libelle && at.Valeur) {
                    const dsc = { label: at.Libelle, value: at.Valeur };
                    if (at.Unite) dsc.unit = at.Unite;
                    menuiserie.description.value.push(dsc);
                }
            });

            let nature = this._find_attribute(menuiserie, "Nature");
            if (nature == 1) {
                opening_type.category = "window";
            }
            else {
                opening_type.category = "door";
                opening_type.z = 0;
            }

            let materiau = this._find_attribute(menuiserie, "Materiau");
            if (materiau == 1)
                opening_type.frame = "wood";
            else if (materiau == 2)
                opening_type.frame = "pvc";
            else
                opening_type.frame = "alu";

            let rcl = this._find_attribute(menuiserie, "RCL");
            if (opening_type.category == 'door' && typeof(rcl) == 'number' && rcl < 0.1)
                opening_type.glazing = "none";
            console.log("fenetre rcl",rcl);

            let Uw = this._find_attribute(menuiserie, "U");
            if (Uw && typeof (Uw) == 'number') {
                opening_type.user_physics = true;
                opening_type.Uw = Uw;
            }

            let Sw = this._find_attribute(menuiserie, "SMen");
            if (Sw && typeof (Sw) == 'number') {
                opening_type.user_physics = true;
                opening_type.Sw = Sw;
            }

            let Tl = this._find_attribute(menuiserie, "TrMen");
            if (Tl && typeof (Tl) == 'number') {
                opening_type.user_physics = true;
                opening_type.Tl = Tl;
            }

            //*** dimensions */
            if (menuiserie.Dimensions.length)
            {
                let dims = menuiserie.Dimensions[0];
                let width = this._find_attribute(dims, "Largeur");
                let height = this._find_attribute(dims, "Hauteur");
                if (typeof(width) == 'number' && typeof(height) == 'number')
                {
                    if (width > 1.3)
                        opening_type.panels = 2;
                    else 
                        opening_type.panels = 1;
                    opening_type.width = width;
                    opening_type.height = height;
                }
            }
        });

        //*** Remove obsolete element types */
        obsolete_opening_types.forEach(ot => {
            if (this._building.get_element_type_count(ot) == 0) {
                var index = this._building.element_types.indexOf(ot);
                if (index >= 0) {
                    this._building.element_types.splice(index, 1);
                    res[2]++;
                }
            }
        });
        return res;
    }

    _find_attribute(element, attribute) {
        const att = element.Attributs.find(at => at.UniqueId == attribute);
        if (!att) return undefined;
        if (typeof (att.Valeur) == 'number' && typeof (att.Unite) == "string" && att.Unite == "cm")
            return att.Valeur * 0.01;
        return att.Valeur;
    }

}


